import { useState, useEffect, useCallback } from "react";
import { Plus, Trash2, X } from "lucide-react";
import { analyticsApi, ApiError } from "@/services/apiClient";
import { useFilterStore, isReadOnlyEnv } from "@/stores/filterStore";
import { fmt } from "@/lib/format";
import { KRW_PER_USD } from "@/lib/timeseries";

// 지출 수기 입력·관리(2026-07-21) — AWS 청구가 리셀러 경유라 API 자동 연동이 불가능해서
// 관리자가 청구서 금액을 직접 넣는다. 추이 탭의 "지출"이 이 입력(+ OpenAI 자동)만으로
// 계산되므로, 여기 입력이 곧 손익 정확도다. 월 청구서는 기간(periodFrom~periodTo)을 주면
// 백엔드가 일할 안분해 주별 그래프에 스파이크가 안 생긴다.
const CATEGORIES = [
  { id: "server",    label: "서버비 (AWS)" },
  { id: "ai",        label: "AI 구독·API료" },
  { id: "labor",     label: "인건비" },
  { id: "marketing", label: "마케팅비" },
  { id: "other",     label: "기타" },
];
const CATEGORY_LABEL = Object.fromEntries(CATEGORIES.map((c) => [c.id, c.label]));

export default function ExpenseManager({ range, onChanged }) {
  const readOnly = isReadOnlyEnv(useFilterStore((s) => s.env));
  const [expenses, setExpenses] = useState(null);
  const [error, setError] = useState(null);
  const [formOpen, setFormOpen] = useState(false);

  const fetchList = useCallback(async () => {
    setError(null);
    try {
      const res = await analyticsApi.expenses.list({ from: range.from, to: range.to });
      setExpenses(res.expenses || []);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    }
  }, [range.from, range.to]);

  useEffect(() => { fetchList(); }, [fetchList]);

  async function handleDelete(row) {
    if (readOnly) { setError("STG 조회 상태에서는 지출 삭제가 비활성화됩니다."); return; }
    try {
      if (row.groupId) {
        // 묶음 삭제는 백엔드가 청구 기간 전체를 스캔해 처리 — 조회 기간이 청구 기간
        // 일부만 걸쳐 있어도 기간 밖 행이 유령으로 남지 않는다.
        await analyticsApi.expenses.removeGroup(row.groupId, {
          periodFrom: row.periodFrom, periodTo: row.periodTo,
        });
      } else {
        await analyticsApi.expenses.remove(row.transactionId, row.date);
      }
      await fetchList();
      onChanged?.();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    }
  }

  // 기간 안분 묶음은 한 행으로 접어서 보여준다(일별 30행 나열은 소음). 금액은 일별 부분합이
  // 아니라 원본 청구 총액(amountTotal) — 조회 기간이 청구 기간 일부만 걸치면 부분합이 총액처럼
  // 보이는 오독이 있었다(2026-07-21 실물 확인에서 발견: 31일 중 21일만 걸쳐 10원이 7원으로).
  const grouped = [];
  const seenGroups = new Set();
  for (const e of expenses || []) {
    if (e.groupId) {
      if (seenGroups.has(e.groupId)) continue;
      seenGroups.add(e.groupId);
      const total = e.amountTotal ?? e.amount;
      const totalDays = Math.round((new Date(e.periodTo) - new Date(e.periodFrom)) / 86400000) + 1;
      grouped.push({
        ...e,
        amount: total,
        amountKrw: e.currency === "USD" ? Math.round(total * KRW_PER_USD) : Math.round(total),
        totalDays,
      });
    } else {
      grouped.push({ ...e, totalDays: 0 });
    }
  }

  return (
    <div className="mt-6 border-t border-ink-100 pt-4">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <h4 className="text-xs font-semibold uppercase tracking-wide text-ink-500">
          지출 수기 입력 <span className="font-normal normal-case text-ink-400">— 추이 탭 "지출"의 원본 (리셀러 경유 AWS 청구·구독료 등)</span>
        </h4>
        <button
          type="button"
          onClick={() => setFormOpen((o) => !o)}
          disabled={readOnly}
          className={`inline-flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-xs font-semibold disabled:opacity-40 disabled:cursor-not-allowed ${
            formOpen ? "bg-ink-200 text-ink-700" : "bg-brand-600 text-white hover:bg-brand-700"
          }`}
        >
          {formOpen ? <X className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />}
          {readOnly ? "STG 조회 전용" : formOpen ? "닫기" : "지출 등록"}
        </button>
      </div>

      {formOpen && <ExpenseForm onSaved={() => { setFormOpen(false); fetchList(); onChanged?.(); }} />}

      {error && <div className="mb-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}
      {expenses !== null && grouped.length === 0 && (
        <div className="rounded-lg border border-ink-100 p-4 text-center text-xs text-ink-400">
          이 기간에 등록된 지출이 없습니다 — 청구서가 나왔다면 "지출 등록"으로 입력해 주세요.
        </div>
      )}
      {grouped.length > 0 && (
        <div className="overflow-x-auto rounded-lg border border-ink-100">
          <table className="min-w-full text-sm">
            <thead className="bg-ink-50">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium text-ink-500">날짜 / 기간</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-ink-500">분류</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-ink-500">항목</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-ink-500">금액</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-ink-500">메모</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-ink-500">입력자</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-ink-500">삭제</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-100">
              {grouped.map((e) => (
                <tr key={e.groupId || e.transactionId} className="hover:bg-ink-50">
                  <td className="whitespace-nowrap px-3 py-2 tabular-nums text-ink-600">
                    {e.groupId ? `${e.periodFrom} ~ ${e.periodTo}` : e.date}
                    {e.groupId && <span className="ml-1 rounded bg-ink-100 px-1 py-0.5 text-[10px] text-ink-500">일할 {e.totalDays}일</span>}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 text-ink-600">{CATEGORY_LABEL[e.category] || e.category}</td>
                  <td className="max-w-[10rem] truncate px-3 py-2 text-ink-700">{e.service || "—"}</td>
                  <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums font-medium text-ink-900">
                    {e.currency === "USD" ? fmt.usd(e.amount) : fmt.krw(e.amount)}
                    {e.currency === "USD" && <span className="ml-1 text-[10px] text-ink-400">≈{fmt.krw(e.amountKrw)}</span>}
                  </td>
                  <td className="max-w-[12rem] truncate px-3 py-2 text-ink-500">{e.memo || "—"}</td>
                  <td className="max-w-[9rem] truncate whitespace-nowrap px-3 py-2 text-xs text-ink-400">{e.createdBy}</td>
                  <td className="whitespace-nowrap px-3 py-2 text-right">
                    <DeleteButton onConfirm={() => handleDelete(e)} grouped={!!e.groupId} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// 삭제는 두 번 클릭 확인 — 지출 삭제는 손익 숫자를 바로 바꾸는 조작이라 오클릭 방지.
function DeleteButton({ onConfirm, grouped }) {
  const [confirming, setConfirming] = useState(false);
  useEffect(() => {
    if (!confirming) return;
    const t = setTimeout(() => setConfirming(false), 3000);
    return () => clearTimeout(t);
  }, [confirming]);
  return (
    <button
      type="button"
      onClick={() => (confirming ? onConfirm() : setConfirming(true))}
      title={grouped ? "일할 안분 묶음 전체 삭제" : "삭제"}
      className={`inline-flex items-center gap-1 rounded-md px-1.5 py-1 text-xs ${
        confirming ? "bg-red-600 text-white" : "text-ink-400 hover:bg-red-50 hover:text-red-600"
      }`}
    >
      <Trash2 className="h-3.5 w-3.5" />
      {confirming && "확인"}
    </button>
  );
}

function ExpenseForm({ onSaved }) {
  const readOnly = isReadOnlyEnv(useFilterStore((s) => s.env));
  const [mode, setMode] = useState("period"); // "period"(월 청구서 일할 안분, 기본) | "single"
  const [periodFrom, setPeriodFrom] = useState("");
  const [periodTo, setPeriodTo] = useState("");
  const [date, setDate] = useState("");
  const [category, setCategory] = useState("server");
  const [service, setService] = useState("");
  const [amount, setAmount] = useState("");
  const [currency, setCurrency] = useState("KRW");
  const [memo, setMemo] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const amt = Number(amount);
  const validAmount = Number.isFinite(amt) && amt > 0;
  const validDates = mode === "period" ? (periodFrom && periodTo && periodFrom <= periodTo) : !!date;

  async function submit() {
    if (readOnly) { setError("STG 조회 상태에서는 지출 등록이 비활성화됩니다."); return; }
    if (!validAmount) { setError("금액을 입력하세요."); return; }
    if (!validDates) { setError(mode === "period" ? "청구 기간을 입력하세요." : "날짜를 입력하세요."); return; }
    setSubmitting(true);
    setError(null);
    try {
      const body = { category, amount: amt, currency, memo: memo.trim(), service: service.trim() };
      if (mode === "period") { body.periodFrom = periodFrom; body.periodTo = periodTo; }
      else { body.date = date; }
      await analyticsApi.expenses.create(body);
      onSaved?.();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  }

  const inputCls = "rounded-md border border-ink-200 px-2 py-1.5 text-sm focus:border-brand-400 focus:outline-none";

  return (
    <div className="mb-3 rounded-xl border border-ink-200 bg-ink-50/50 p-3">
      <div className="mb-2 flex flex-wrap items-center gap-2 text-xs">
        <label className="flex items-center gap-1.5">
          <input type="radio" checked={mode === "period"} onChange={() => setMode("period")} />
          청구 기간 입력 (일할 안분 — 월 청구서 권장)
        </label>
        <label className="flex items-center gap-1.5">
          <input type="radio" checked={mode === "single"} onChange={() => setMode("single")} />
          단일 날짜
        </label>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {mode === "period" ? (
          <>
            <input type="date" value={periodFrom} onChange={(e) => setPeriodFrom(e.target.value)} className={inputCls} />
            <span className="text-xs text-ink-400">~</span>
            <input type="date" value={periodTo} onChange={(e) => setPeriodTo(e.target.value)} className={inputCls} />
          </>
        ) : (
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className={inputCls} />
        )}
        <select value={category} onChange={(e) => setCategory(e.target.value)} className={inputCls}>
          {CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
        </select>
        <input
          type="text" value={service} onChange={(e) => setService(e.target.value)}
          placeholder="항목 (예: AWS 7월분, Claude Max)" className={`${inputCls} w-44`}
        />
        <input
          type="number" min="0" step="any" value={amount} onChange={(e) => setAmount(e.target.value)}
          placeholder="금액" className={`${inputCls} w-28 tabular-nums`}
        />
        <select value={currency} onChange={(e) => setCurrency(e.target.value)} className={inputCls}>
          <option value="KRW">KRW</option>
          <option value="USD">USD</option>
        </select>
        <input
          type="text" value={memo} onChange={(e) => setMemo(e.target.value)}
          placeholder="메모 (선택)" className={`${inputCls} w-40`}
        />
        <button
          type="button" onClick={submit} disabled={submitting || readOnly}
          className="rounded-lg bg-ink-900 px-3 py-1.5 text-xs font-semibold text-white hover:bg-ink-700 disabled:opacity-50"
        >
          {submitting ? "저장 중…" : readOnly ? "STG 조회 전용" : "저장"}
        </button>
      </div>
      {validAmount && currency === "USD" && (
        <p className="mt-1.5 text-xs text-ink-400">≈ {fmt.krw(amt * KRW_PER_USD)} (환율 {KRW_PER_USD.toLocaleString()}원/USD 고정)</p>
      )}
      {mode === "period" && validAmount && validDates && (() => {
        const days = Math.round((new Date(periodTo) - new Date(periodFrom)) / 86400000) + 1;
        return <p className="mt-1 text-xs text-ink-400">{days}일로 안분 — 하루 {currency === "USD" ? fmt.usd(amt / days) : fmt.krw(amt / days)}씩 계상됩니다.</p>;
      })()}
      {error && <p className="mt-1.5 text-xs text-red-600">{error}</p>}
    </div>
  );
}
